/**
 * ADR-0214 Part 2 revision 3 — the Activity dashboard and its detail screens:
 * the responses and the words their values are shown in (screen spec §3B.7).
 * The dashboard shows names and bars only; a card's detail screen shows the
 * counts in plain columns. An action is one thing a person did in Atlas.
 */

export type CardKey = "concepts" | "users" | "knowledge" | "gaps" | "blocks" | "objects" | "asked-again" | "quiet" | "problems";
export type Tone = "activity" | "helped" | "gap" | "alarm";

export interface CardHead {
  key: CardKey;
  title: string;
  by: string;
  tone: Tone;
  empty: string;
  note: string | null;
}

export type Audience = "mine" | "people" | "everyone";
export type Purpose = "business" | "development" | "both";

/** Who a read is about and which work, as the hub resolved it for the signed-in person. */
export interface ActivityScopeView {
  audience: Audience;
  audiences: Audience[];
  purpose: Purpose;
  /** Administrators only: the Work control. */
  purpose_choice: boolean;
}

export const AUDIENCE_LABEL: Readonly<Record<Audience, string>> = { mine: "Mine", people: "My people", everyone: "Everyone" };

/** Everyone (§3B.6) carries these cards only; the cards that name people are absent, not empty. Mirrors the hub's EVERYONE_CARDS. */
export const EVERYONE_CARDS: readonly CardKey[] = ["concepts", "knowledge", "gaps", "objects", "asked-again"];

/**
 * Does this audience carry this card? An empty audience is the hub's default for
 * the person (My people or Mine), which carries every card. A card Everyone does
 * not carry has no detail screen under Everyone: the person goes to Activity instead.
 */
export function cardCarried(card: string | undefined, audience: string): boolean {
  return audience !== "everyone" || EVERYONE_CARDS.includes(card as CardKey);
}
export const PURPOSE_LABEL: Readonly<Record<Purpose, string>> = { business: "Business", development: "Development", both: "Both" };
export const workLabel = (p: Purpose) => (p === "both" ? "business and development work" : `${p} work`);

export interface ActivityDashboardResponse extends ActivityScopeView {
  time_zone: string;
  quiet_days: number;
  cards: Array<CardHead & { rows: Array<{ name: string; kind: string | null; value: number }> }>;
}

export type Cell =
  | { count: number }
  | { at: string | null }
  | { text: string }
  | { person: string; key: string | null }
  | { subjects: Array<{ title: string; number: number }>; more: number };

export interface Column {
  label: string;
  type: "count" | "time" | "text" | "person" | "subjects";
}

export interface Panel {
  reason: string;
  person: { person: string; key: string } | null;
  columns: Column[];
  rows: Cell[][];
  more: number;
  /** The one-person drill's further sections (owner, 2026-10-20): blocked, problems, searches that found nothing. */
  sections?: PanelSection[];
}

export interface PanelSection {
  title: string;
  empty: string;
  columns: Column[];
  rows: Cell[][];
  more: number;
}

export interface ActivityCardResponse extends ActivityScopeView {
  time_zone: string;
  card: CardHead;
  columns: Column[];
  rows: Array<{ name: string; kind: string | null; value: number; cells: Cell[]; panel: Panel }>;
  truncated: boolean;
}

export const ACTION_DEFINITION =
  "An action is one thing a person did in Atlas: a question to the assistant, a query, opening a report, or a change in the console.";

/**
 * Bar colour carries meaning: activity blue, knowledge that helped green, gaps
 * and gone quiet amber, blocks and problems red. The house palette's blue,
 * green, amber and red tokens (declared for the RASCI chips; styles.css is not
 * touched by this screen).
 */
export const TONE_COLOR: Readonly<Record<Tone, string>> = {
  activity: "var(--rasci-r)",
  helped: "var(--rasci-s)",
  gap: "var(--rasci-c)",
  alarm: "var(--rasci-a)",
};
export const barFill = (tone: Tone) => `color-mix(in srgb, ${TONE_COLOR[tone]} 60%, transparent)`;

/** A bar's length, relative to the list's top row (never shorter than a sliver). */
export function barWidth(value: number, top: number): string {
  if (top <= 0 || value <= 0) return "0%";
  return `${Math.max(2, Math.round((value / top) * 100))}%`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-09-18" → "Sep 18, 2026": a calendar day, read as written (no time zone shift). */
export function dayLabel(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

export const periodLabel = (from: string, to: string) => `${dayLabel(from)} to ${dayLabel(to)}`;

/** The keys the dashboard and its detail screens keep in the address. */
export function rangeQuery(from: string, to: string, quietDays: string, audience = "", purpose = ""): string {
  const q = new URLSearchParams({ from, to, quiet_days: quietDays });
  if (audience) q.set("audience", audience);
  if (purpose) q.set("purpose", purpose);
  return q.toString();
}
